import { store } from '../redux/store';
import { updateReduxAction } from '../redux/actions';
import { IUserInfo } from '../interfaces/interfaces';

//This module owns our own auth data, independent of oidc-client/UserManager.
//IdentityService.ts writes here once (right after signinRedirectCallback), and everything
//else in the app - components AND plain utility functions - reads from here instead of
//needing a UserManager instance.

const AUTH_STORAGE_KEY = 'specmate.auth';

export interface StoredAuth {
  access_token: string;
  expires_at: number; //unix timestamp (seconds)
  profile: any;
}

export function saveAuth(access_token: string, expires_at: number, profile: any) {
  const auth: StoredAuth = { access_token, expires_at, profile };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
}

export function getAuth(): StoredAuth | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function clearAuth() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export function isAuthValid(auth: StoredAuth | null = getAuth()): boolean {
  const nowInSeconds = Date.now() / 1000;
  return !!auth && !!auth.access_token && auth.expires_at > nowInSeconds;
}

export function getAccessToken(): string {
  const auth = getAuth();
  return isAuthValid(auth) ? auth!.access_token : '';
}

export function getUserRoles(profile: any): string[] {
  let client_id = process.env.REACT_APP_IDENTITY_CLIENTID;
  let rolesArray = [];

  if (profile) {
    //Get the auto-assigned role from the IdentityService "UserType" variable (employee, rep, customer)
    if (profile.UserType) {
      rolesArray.push(profile.UserType.toLowerCase());
    }

    //Get custom roles from the IdentityService profiledata (any new roles we have manually assigned to people)
    if (profile.profiledata) {
      try {
        let obj = JSON.parse(profile.profiledata);
        let userRoles = obj.UserRolesArr;

        //Get the roles matching the client_id for this app.
        //NOTE: there are different client_ids for dev, qa, stg, etc...
        let lowryRoles = userRoles?.find((x: any) => x.C === client_id);
        if (lowryRoles) {
          for (const role of lowryRoles.R) {
            rolesArray.push(role.toLowerCase());
          }
        }
      } catch {
        //Malformed/unexpected profiledata shape - fall back to just the UserType role above.
      }
    }
  }
  return rolesArray;
}

export function getUserInfo(profile: any): IUserInfo {
  let userInfo: IUserInfo = {
    userId: '',
    userType: '',
    firstName: '',
    lastName: '',
    email: '',
    officeMembershipsArr: []
  };

  if (profile) {
    userInfo.userId = profile.sub;
    userInfo.firstName = profile.FirstName;
    userInfo.lastName = profile.LastName;
    userInfo.email = profile.Email;
    //Get the auto-assigned role from the IdentityService "UserType" variable (employee, rep, customer)
    if (profile.UserType) {
      userInfo.userType = profile.UserType.toLowerCase();
    }
    //Get custom officeMemberShipArr from the IdentityService profiledata
    if (profile.profiledata) {
      try {
        let obj = JSON.parse(profile.profiledata);
        if (obj.OfficeMembershipsArr != null && obj.OfficeMembershipsArr.length > 0) {
          userInfo.officeMembershipsArr = obj.OfficeMembershipsArr;
        }
      } catch {
        //Malformed/unexpected profiledata shape - leave officeMembershipsArr empty.
      }
    }
  }
  return userInfo;
}

//Populates reduxAuth from whatever is currently valid in localStorage.  Called on app mount
//(to survive a page refresh) and right after a fresh login.
export function syncAuthToRedux() {
  const auth = getAuth();
  if (!isAuthValid(auth)) {
    return;
  }
  const profile = auth!.profile;
  const rolesArray = getUserRoles(profile);
  let obj: any = {};
  try {
    obj = JSON.parse(profile.profiledata);
  } catch {
    //Malformed/unexpected profiledata shape - fall back to an empty object below.
  }

  const firstOffice = obj.OfficeMembershipsArr?.[0];
  const officeId = firstOffice?.OfficeId ?? null;
  const officeName = firstOffice?.OfficeName ?? null;

  store.dispatch(
    updateReduxAction('UPDATE_AUTH', {
      userId: profile.sub,
      access_token: auth!.access_token,
      userRoles: rolesArray,
      state: obj.State,
      postalCode: obj.PostalCode,
      officeId: officeId,
      officeName: officeName,
      userFirstName: obj.FirstName,
      userLastName: obj.LastName,
      userType: obj.UserType
    })
  );
}

export function clearAuthRedux() {
  store.dispatch(
    updateReduxAction('UPDATE_AUTH', {
      userId: '',
      access_token: '',
      userRoles: [],
      state: '',
      postalCode: '',
      userFirstName: '',
      userLastName: '',
      userType: ''
    })
  );
}
